// src/theme/creatorTheme.js
// Dark mode for the signed-in creator app — and only there.
//
// The preference ('light' | 'dark' | 'system') lives in localStorage, per
// device; light is the default. It takes effect through useCreatorTheme(),
// which ProtectedRoute calls for every non-admin route: while such a page is
// mounted <html> carries `dark`, and on unmount the class comes off. That
// is what keeps public pages, checkout, storefronts and admin light no matter
// what the creator picked — they never mount a ProtectedRoute (admin does, with
// requireAdmin, and is skipped).
//
// The colours themselves are src/theme/dark.generated.css (built by
// scripts/gen-dark-theme.js) plus src/theme/dark.css.
import { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { safeLocalStorage } from '../utils/safeStorage';

const KEY = 'ml_theme';
const CHANGE_EVENT = 'ml-theme-change';
export const THEME_OPTIONS = ['light', 'dark', 'system'];

// Full-screen builders render a live preview of the creator's own site, which
// must look the way buyers will see it. Same list as AppLayout's isEditorRoute.
const LIGHT_ONLY_ROUTES = [
  /^\/pages\/[^/]+\/edit/,
  /^\/events\/(new|[^/]+\/edit)$/,
  /^\/quizzes\/(new|[^/]+\/edit)$/,
  /^\/storefront$/,
];

export function getThemePreference() {
  const v = safeLocalStorage.getItem(KEY);
  return THEME_OPTIONS.includes(v) ? v : 'light';
}

export function setThemePreference(value) {
  if (!THEME_OPTIONS.includes(value)) return;
  safeLocalStorage.setItem(KEY, value);
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: value }));
}

const systemQuery = () =>
  (typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null);

/** The stored preference, kept in sync across components and tabs. */
export function useThemePreference() {
  const [pref, setPref] = useState(getThemePreference);
  useEffect(() => {
    const sync = () => setPref(getThemePreference());
    const onStorage = (e) => { if (e.key === KEY) sync(); };
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  return [pref, setThemePreference];
}

function useSystemDark() {
  const [dark, setDark] = useState(() => !!systemQuery()?.matches);
  useEffect(() => {
    const mq = systemQuery();
    if (!mq) return undefined;
    const onChange = (e) => setDark(e.matches);
    // Safari < 14 only has addListener.
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return dark;
}

// The status-bar colour in the installed app (public/index.html sets the light one).
let lightThemeColor = null;
function setThemeColor(dark) {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) return;
  if (lightThemeColor == null) lightThemeColor = meta.getAttribute('content');
  meta.setAttribute('content', dark ? '#0e1311' : lightThemeColor);
}

/**
 * Puts `dark` on <html> while the calling component is mounted, if the
 * creator chose dark (or "match my device" and the device is dark).
 * Layout effect, so the class lands before the first paint of the page.
 */
export function useCreatorTheme(enabled) {
  const [pref] = useThemePreference();
  const systemDark = useSystemDark();
  const { pathname } = useLocation();
  const lightOnly = LIGHT_ONLY_ROUTES.some((re) => re.test(pathname));
  const dark = enabled && !lightOnly && (pref === 'dark' || (pref === 'system' && systemDark));

  useLayoutEffect(() => {
    if (!dark) return undefined;
    const root = document.documentElement;
    root.classList.add('dark');
    setThemeColor(true);
    return () => {
      root.classList.remove('dark');
      setThemeColor(false);
    };
  }, [dark]);
}
